/**
 * SendComposerCc — the shared CC chip row for the send composer (IDI-134).
 *
 * RULED (Adrian, 2026-10-13, after the co-UAT with Sarah):
 *   - The CC row is pre-populated with removable chips (×) for
 *       · the SIGNED-IN user (useAuth().user.email; 08-26 ruling), and
 *       · the document's assigned salesperson(s).
 *   - Others are added like an email client: type-ahead over the dealer's
 *     users (settings list_users&scope=tenant), or any typed email address.
 *
 * BUILD CONTRACT (2026-10-20):
 *   - Salesperson chips only for salespeople WITH A LOGIN, i.e. who resolve to
 *     an active user in the tenant directory; no-login salespeople wait for
 *     IDI-497.
 *   - The type-ahead lists the quote tenant's active users of any role, for
 *     every caller including super-admins (scope=tenant on the backend).
 *   - At most MAX_CC addresses; the 11th is refused with a message.
 *   - CC'd people get the message + attachments, NEVER a signing link
 *     (the backend sends them a separate copy without one).
 *
 * Generic on purpose: quotes use it today. Change orders and invoices adopt it
 * when their sends get mailer wiring (IDI-137). Nothing here is quote-specific;
 * callers pass whatever `salespersons` / `salespersonIds` their record carries.
 *
 * Controlled component:
 *   value     string[] | null   lowercase email addresses. `null` means "not
 *                               seeded yet": the component seeds once, as soon
 *                               as the user directory has loaded (or failed),
 *                               and from then on never re-seeds. A user who
 *                               removes their own chip and closes/reopens the
 *                               modal does not get it back.
 *   onChange  (string[]) => void
 */
import React, { useEffect, useMemo, useState } from 'react';
import TypeaheadChips from '../ui/TypeaheadChips';
import { useOptionalAuth } from '../../contexts/AuthContext';
import { settingsAPI } from '../../services/api';
import { displayName } from '../../utils/displayName';
import { isFieldTech } from '../../utils/isFieldTech';

// Deliberately simple. The backend re-validates (net/mail) and the mailer
// guard decides deliverability; this check only catches typos.
const EMAIL_RE = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/;

/** Max CC addresses per send (Adrian 10-20; the backend caps at 10 too). */
export const MAX_CC = 10;

export function isValidEmail(s) {
  return EMAIL_RE.test(String(s || '').trim());
}

function normalizeEmail(s) {
  return String(s || '').trim().toLowerCase();
}

const INACTIVE = new Set(['inactive', 'deactivated', 'disabled', 'archived']);

function isActiveUser(u) {
  if (!u) return false;
  if (u.enabled === false) return false;
  return !INACTIVE.has(String(u.status || '').trim().toLowerCase());
}

/**
 * Adds `cc` to a send_composer payload only when at least one valid address
 * remains (IDI-134 acceptance: unchecked/removed = key omitted entirely, never
 * an empty array). Returns a new object; the input is not mutated.
 */
export function withComposerCc(composer, ccEmails) {
  const cc = dedupeEmails(ccEmails);
  // eslint-disable-next-line no-unused-vars
  const { cc: _drop, ...rest } = composer || {};
  return cc.length > 0 ? { ...rest, cc } : rest;
}

/** Case-insensitive, order-preserving dedup of valid addresses. */
export function dedupeEmails(list) {
  const seen = new Set();
  const out = [];
  for (const raw of list || []) {
    const e = normalizeEmail(raw);
    if (!e || !isValidEmail(e) || seen.has(e)) continue;
    seen.add(e);
    out.push(e);
  }
  return out;
}

/**
 * The seed list: signed-in user first, then the salesperson, in IDI-164's
 * order (IDI-548): the picked salespersons (salespersons[] / salesperson_ids)
 * when there are any, else the quote's assignee (`assigneeEmail`, resolved by
 * the backend from assignedTo / legacy Quote_AssignedTo). Never the creator
 * (IDI-342). Each salesperson must have a login. A salesperson becomes a
 * chip only through the tenant user directory:
 * by id (user id = Cognito username, which is what salespersons[].id /
 * salesperson_ids hold — IDI-138), or by a record email that matches an active
 * directory user. A salesperson with no matching active user (a no-login
 * salesperson, IDI-497) is skipped quietly. Capped at MAX_CC.
 */
export function computeCcSeeds({ userEmail, salespersons, salespersonIds, assigneeEmail, users }) {
  const byId = new Map();
  const byEmail = new Map();
  for (const u of users || []) {
    if (!isActiveUser(u)) continue;
    if (u.id != null) byId.set(String(u.id), u);
    const e = normalizeEmail(u.email);
    if (e) byEmail.set(e, u);
  }
  const seeds = [userEmail];
  const resolve = (id, email) => {
    const u = (id != null && byId.get(String(id))) || (email && byEmail.get(normalizeEmail(email)));
    if (u?.email) seeds.push(u.email);
  };
  const picked = (salespersons || []).filter(Boolean);
  const pickedIds = (salespersonIds || []).filter((id) => id != null && String(id).trim() !== '');
  for (const sp of picked) {
    if (typeof sp === 'string') resolve(sp, null);
    else resolve(sp.id, sp.email);
  }
  for (const id of pickedIds) resolve(id, null);
  if (picked.length === 0 && pickedIds.length === 0 && assigneeEmail) {
    resolve(null, assigneeEmail);
  }
  return dedupeEmails(seeds).slice(0, MAX_CC);
}

export default function SendComposerCc({
  value,
  onChange,
  salespersons,
  salespersonIds,
  assigneeEmail,
  disabled = false,
  id = 'send-composer-cc',
  // IDI-596: the line under the CC row. Quotes keep the default; the invoice
  // composer passes its own (an invoice has no signing link to mention).
  hint = "CC'd people get a copy with the attachments, but no signing link.",
}) {
  const auth = useOptionalAuth();
  const userEmail = auth?.user?.email || '';
  const [users, setUsers] = useState(null); // null = loading
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => settingsAPI.getTenantUsers())
      .then((list) => { if (!cancelled) setUsers(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setUsers([]); }); // type-ahead degrades; typed addresses still work
    return () => { cancelled = true; };
  }, []);

  // Seed exactly once, after the directory resolves (so salesperson ids can
  // be turned into addresses).
  useEffect(() => {
    if (value != null || users == null) return;
    onChange?.(computeCcSeeds({ userEmail, salespersons, salespersonIds, assigneeEmail, users }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, value]);

  const options = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const u of users || []) {
      if (!isActiveUser(u)) continue;
      // IDI-582 S4/D5: field techs can't see quotes or invoices, so the
      // type-ahead leaves them out. A typed address is still accepted.
      if (isFieldTech(u)) continue;
      const email = normalizeEmail(u.email);
      if (!email || !isValidEmail(email) || seen.has(email)) continue;
      seen.add(email);
      // IDI-548: the chip shows the name (IDI-550 rule: profile first + last
      // → invite name → the address); hover and the type-ahead list keep
      // "Name <email>" so two people with one name can be told apart.
      const name = displayName({ ...u, email });
      const full = name && name !== email ? `${name} <${email}>` : email;
      out.push({ value: email, label: full, chipLabel: name || email, title: full });
    }
    return out;
  }, [users]);

  const current = value || [];

  const capMessage = `You can CC at most ${MAX_CC} people on one send.`;

  const handleChange = (next) => {
    const cleaned = dedupeEmails(next);
    if (cleaned.length > MAX_CC) {
      setError(capMessage);
      return;
    }
    setError('');
    onChange?.(cleaned);
  };

  const handleCreate = (typed) => {
    const email = normalizeEmail(typed);
    if (!isValidEmail(email)) {
      setError(`"${String(typed).trim()}" is not a valid email address.`);
      return;
    }
    if (current.includes(email)) {
      setError('');
      return;
    }
    if (current.length >= MAX_CC) {
      setError(capMessage);
      return;
    }
    setError('');
    onChange?.([...current, email]);
  };

  return (
    <div data-portica-role="send-composer-cc">
      <label
        htmlFor={id}
        className="block text-xs font-medium mb-1"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        CC
      </label>
      <TypeaheadChips
        id={id}
        value={current}
        onChange={handleChange}
        options={options}
        allowCreate
        onCreate={handleCreate}
        disabled={disabled}
        invalid={!!error}
        placeholder="Add a teammate or type an email"
        emptyMessage={users == null ? 'Loading users…' : 'No matching users — type a full email to add it'}
        aria-label="CC"
      />
      {error ? (
        <p className="mt-1 text-xs" role="alert" style={{ color: 'var(--color-error)' }}>
          {error}
        </p>
      ) : (
        <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {hint}
        </p>
      )}
    </div>
  );
}
